import {getTestProps} from '@benchling/js-test-utils';
import {Select} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useEffect} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {getChatFilePreviewUrl} from 'llm/chat-library/ChatDAO';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {
  GRAY,
  GRAY_DARK,
  GRAY_DARKER,
  GRAY_LIGHT,
  GRAY_LIGHTER,
  NEAR_BLACK,
  WHITE,
  hexToRgba,
} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import type {ChatLayoutProps} from './ChatLayoutRegistry';

/**
 * File info stored in chat application state for Legacy Data Import.
 */
export interface UploadedFileInfo {
  api_identifier: string;
  media_type: string;
  filename: string;
}

/** Which panels are visible */
type PanelVisibility = 'all' | 'source-draft' | 'draft-chat';

/**
 * Three-panel layout for Legacy Data Import chat view with panel visibility toggles.
 *
 * Shows: chat messages | draft document | source file preview
 *
 * Users can toggle between:
 * - All 3 panels (default)
 * - Source + Draft only (for comparing original to transcription)
 * - Draft + Chat only (for reviewing agent conversation)
 */
export function LegacyDataImportSideBySideLayout({
  chatContent,
  artifacts,
  isEditable,
}: ChatLayoutProps): JSX.Element {
  const {primaryArtifact, secondaryArtifact} = artifacts;

  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);
  const [panelVisibility, setPanelVisibility] = useState<PanelVisibility>('all');

  // Expand sidepanel on mount to show three-panel layout
  useEffect(() => {
    SidepanelActions.expand();
  }, []);

  const showChat = panelVisibility === 'all' || panelVisibility === 'draft-chat';
  const showSource = panelVisibility === 'all' || panelVisibility === 'source-draft';

  // Calculate panel sizes based on visibility
  const getPanelSizes = () => {
    if (panelVisibility === 'all') {
      return {chat: 25, document: 50, source: 25};
    } else if (panelVisibility === 'source-draft') {
      return {chat: 0, document: 50, source: 50};
    } else {
      return {chat: 40, document: 60, source: 0};
    }
  };
  const sizes = getPanelSizes();

  // Render the primary artifact (document)
  const documentContent = primaryArtifact ? (
    <primaryArtifact.Component data={primaryArtifact.data} editable={isEditable} />
  ) : (
    <div
      className={css(styles.emptyPanel)}
      {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'no-document'})}
    >
      <span className={css(styles.emptyText)}>No draft document available</span>
    </div>
  );

  // Render the secondary artifact (source file)
  const sourceFileContent = secondaryArtifact ? (
    <secondaryArtifact.Component data={secondaryArtifact.data} editable={false} />
  ) : (
    <div
      className={css(styles.emptyPanel)}
      {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'no-source-file'})}
    >
      <span className={css(styles.emptyText)}>No source file available</span>
    </div>
  );

  return (
    <div
      className={css(styles.sideBySideContainer)}
      {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'root'})}
    >
      {/* Toolbar with panel toggles */}
      <div
        className={css(styles.toolbar)}
        {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'toolbar'})}
      >
        <Select
          size="xs"
          variant="unstyled"
          value={panelVisibility}
          onChange={(value) => {
            if (value) {
              setPanelVisibility(value as PanelVisibility);
            }
          }}
          withinPortal={true}
          icon={<BenchlingIcon icon="columns" className={css(styles.selectIcon)} />}
          rightSection={<BenchlingIcon icon="chevron-down" className={css(styles.selectChevron)} />}
          classNames={{
            input: css(styles.selectInput),
            icon: css(styles.selectIconWrapper),
            rightSection: css(styles.selectRightSection),
            dropdown: css(styles.selectDropdown),
            item: css(styles.selectItem),
          }}
          {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'view-select'})}
          data={[
            {value: 'all', label: 'Show All'},
            {value: 'source-draft', label: 'Compare'},
            {value: 'draft-chat', label: 'Chat'},
          ]}
        />
      </div>

      {/* Key forces remount when panel configuration changes to avoid react-resizable-panels index errors */}
      <PanelGroup key={panelVisibility} direction="horizontal" style={{width: '100%', height: '100%'}}>
        {/* Left panel - Chat messages */}
        {showChat && (
          <>
            <Panel
              defaultSize={sizes.chat}
              minSize={15}
              maxSize={panelVisibility === 'draft-chat' ? 50 : 40}
              className={css(styles.panel)}
            >
              <div
                {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'chat-panel'})}
                className={css(styles.panelWrapper)}
              >
                <div
                  className={css(styles.scrollableContent)}
                  {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'chat-content'})}
                >
                  {chatContent}
                </div>
              </div>
            </Panel>

            {/* Left resize handle */}
            <PanelResizeHandle
              className={css(styles.resizer, isDraggingLeft && styles.resizerActive)}
              onDragging={setIsDraggingLeft}
            >
              <div
                {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'resizer-left'})}
                className={css(styles.resizerWrapper)}
              >
                <div
                  className={css(styles.resizerHandle)}
                  {...getTestProps({
                    component: 'LegacyDataImportSideBySideLayout',
                    element: 'resizer-handle-left',
                  })}
                />
              </div>
            </PanelResizeHandle>
          </>
        )}

        {/* Center panel - Draft document */}
        <Panel
          defaultSize={sizes.document}
          minSize={30}
          maxSize={panelVisibility === 'all' ? 60 : 70}
          className={css(styles.panel)}
        >
          <div
            {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'document-panel'})}
            className={css(styles.panelWrapper)}
          >
            <div
              className={css(styles.scrollableContent)}
              {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'document-content'})}
            >
              {documentContent}
            </div>
          </div>
        </Panel>

        {/* Right resize handle */}
        {showSource && (
          <>
            <PanelResizeHandle
              className={css(styles.resizer, isDraggingRight && styles.resizerActive)}
              onDragging={setIsDraggingRight}
            >
              <div
                {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'resizer-right'})}
                className={css(styles.resizerWrapper)}
              >
                <div
                  className={css(styles.resizerHandle)}
                  {...getTestProps({
                    component: 'LegacyDataImportSideBySideLayout',
                    element: 'resizer-handle-right',
                  })}
                />
              </div>
            </PanelResizeHandle>

            {/* Right panel - Source file preview */}
            <Panel
              defaultSize={sizes.source}
              minSize={15}
              maxSize={panelVisibility === 'source-draft' ? 60 : 40}
              className={css(styles.panel)}
            >
              <div
                {...getTestProps({component: 'LegacyDataImportSideBySideLayout', element: 'source-panel'})}
                className={css(styles.panelWrapper)}
              >
                <div
                  className={css(styles.scrollableContent)}
                  {...getTestProps({
                    component: 'LegacyDataImportSideBySideLayout',
                    element: 'source-content',
                  })}
                >
                  {sourceFileContent}
                </div>
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>
    </div>
  );
}

// ============================================================================
// Source File Preview Component
// ============================================================================

interface LegacyDataImportSourceFilePreviewProps {
  chatSessionId: string;
  fileInfo: UploadedFileInfo;
}

/**
 * Map media_type to EXTERNAL_FILE_TYPE for preview rendering.
 * Only PDF and image files are currently supported for preview.
 */
function getPreviewType(mediaType: string): EXTERNAL_FILE_TYPE {
  if (mediaType.startsWith('image/')) {
    return EXTERNAL_FILE_TYPE.IMAGE;
  }
  if (mediaType === 'application/pdf') {
    return EXTERNAL_FILE_TYPE.PDF;
  }
  return EXTERNAL_FILE_TYPE.NOT_PREVIEWABLE;
}

/**
 * Source file preview component for Legacy Data Import chat view.
 *
 * Renders the original uploaded file in the right panel of the three-panel layout.
 * Uses the chat file preview endpoint which redirects to a signed S3 URL.
 *
 * Supported file types:
 * - PDF - iframe with preview URL (browser follows redirect)
 * - Images (png/jpeg/gif/etc) - img tag with preview URL
 * - Other - "No preview available" fallback
 */
export function LegacyDataImportSourceFilePreview({
  chatSessionId,
  fileInfo,
}: LegacyDataImportSourceFilePreviewProps): JSX.Element {
  const previewUrl = getChatFilePreviewUrl(chatSessionId, fileInfo.api_identifier);
  const previewType = getPreviewType(fileInfo.media_type);

  return (
    <div
      className={css(styles.sourceFileContainer)}
      {...getTestProps({component: 'LegacyDataImportSourceFilePreview', element: 'root'})}
    >
      <div className={css(styles.sourceFileHeader)}>
        <BenchlingIcon icon="file" className={css(styles.sourceFileIcon)} />
        <span className={css(styles.sourceFileFilename)} title={fileInfo.filename}>
          {fileInfo.filename}
        </span>
      </div>
      <div className={css(styles.sourceFilePreviewContainer)}>
        <FilePreview previewUrl={previewUrl} previewType={previewType} />
      </div>
    </div>
  );
}

/**
 * Renders the file preview using the preview URL.
 * The browser will follow the redirect from the preview endpoint to S3.
 */
function FilePreview({
  previewUrl,
  previewType,
}: {
  previewUrl: string;
  previewType: EXTERNAL_FILE_TYPE;
}): JSX.Element {
  switch (previewType) {
    case EXTERNAL_FILE_TYPE.IMAGE:
      return (
        <div className={css(styles.sourceFileImageWrapper)}>
          <img
            src={previewUrl}
            className={css(styles.sourceFileImage)}
            alt="Source file preview"
            {...getTestProps({component: 'LegacyDataImportSourceFilePreview', element: 'image'})}
          />
        </div>
      );
    case EXTERNAL_FILE_TYPE.PDF:
      return (
        <iframe
          src={previewUrl}
          className={css(styles.sourceFileIframe)}
          title="PDF preview"
          // Sandbox for security - PDFs can contain JavaScript
          sandbox="allow-scripts allow-same-origin"
          {...getTestProps({component: 'LegacyDataImportSourceFilePreview', element: 'pdf-iframe'})}
        />
      );
    default:
      return (
        <div className={css(styles.sourceFileNoPreview)}>
          <BenchlingIcon icon="file" className={css(styles.sourceFileNoPreviewIcon)} />
          <p className={css(styles.sourceFileNoPreviewText)}>No preview available yet for this file type.</p>
        </div>
      );
  }
}

const styles = StyleSheet.create({
  sideBySideContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '6px 12px',
    backgroundColor: WHITE,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flexShrink: 0,
  },
  panel: {
    height: '100%',
  },
  panelWrapper: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: WHITE,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  resizer: {
    width: '12px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s ease',
    ':hover': {
      backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.3)})`,
    },
    boxShadow: `2px 0 8px 0 ${GRAY}, inset -1px 0 0 0 ${GRAY_LIGHT}`,
  },
  resizerWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resizerActive: {
    backgroundColor: `rgba(${hexToRgba(GRAY_LIGHT, 0.5)})`,
  },
  resizerHandle: {
    width: '2px',
    height: '40px',
    backgroundColor: GRAY_LIGHT,
    borderRadius: '2px',
    transition: 'all 0.15s ease',
  },
  emptyPanel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: GRAY_LIGHTER,
    borderRadius: 4,
    border: `1px dashed ${GRAY_LIGHT}`,
    margin: 16,
  },
  emptyText: {
    color: GRAY_LIGHT,
    fontSize: 14,
  },
  selectInput: {
    width: 120,
    height: 28,
    minHeight: 28,
    fontSize: 12,
    fontWeight: 500,
    color: NEAR_BLACK,
    backgroundColor: WHITE,
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 6,
    paddingLeft: 36,
    paddingRight: 28,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    ':hover': {
      borderColor: GRAY,
    },
    ':focus': {
      borderColor: GRAY_DARK,
      boxShadow: `0 0 0 2px ${GRAY_LIGHTER}`,
    },
  },
  selectIconWrapper: {
    width: 32,
    paddingLeft: 8,
    pointerEvents: 'none',
  },
  selectRightSection: {
    width: 28,
    pointerEvents: 'none',
  },
  selectDropdown: {
    borderRadius: 8,
    border: `1px solid ${GRAY_LIGHT}`,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  selectItem: {
    fontSize: 12,
    padding: '8px 12px',
    borderRadius: 4,
  },
  selectIcon: {
    fontSize: 12,
    color: GRAY_DARK,
  },
  selectChevron: {
    fontSize: 10,
    color: GRAY_DARK,
  },
  // Source file preview styles
  sourceFileContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  sourceFileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    backgroundColor: GRAY_LIGHTER,
    flexShrink: 0,
  },
  sourceFileIcon: {
    color: NEAR_BLACK,
    flexShrink: 0,
  },
  sourceFileFilename: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: NEAR_BLACK,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sourceFilePreviewContainer: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  sourceFileImageWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    backgroundColor: GRAY_LIGHTER,
  },
  sourceFileImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  sourceFileIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  sourceFileNoPreview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: 24,
    textAlign: 'center',
  },
  sourceFileNoPreviewIcon: {
    fontSize: 48,
    color: GRAY_DARKER,
    marginBottom: 16,
  },
  sourceFileNoPreviewText: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
  },
});
